import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, ExternalLink, Quote, TrendingUp, Globe2, 
  Home, Users, Scale, DollarSign, Building2, Lightbulb,
  BookOpen, GraduationCap, Share2, Twitter, Linkedin, Facebook, Mail
} from 'lucide-react';

const ResearchArticlePage = () => {
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = "How Athletes' Taxes Can Solve the Affordable Housing Crisis";

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    email: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent('Read this important research on solving the housing crisis: ' + shareUrl)}`
  };

  const jockTaxTable = [
    { opponent: 'Carolina Panthers', state: 'North Carolina', jockTax: '$50,808', georgiaTax: '$11,173', total: '$61,981' },
    { opponent: 'New Orleans Saints', state: 'Louisiana', jockTax: '$65,451', georgiaTax: '$0', total: '$65,451' },
    { opponent: 'Tampa Bay Buccaneers', state: 'Florida', jockTax: '$0', georgiaTax: '$61,981', total: '$61,981' },
    { opponent: 'Denver Broncos', state: 'Colorado', jockTax: '$49,676', georgiaTax: '$12,305', total: '$61,981' },
    { opponent: 'Las Vegas Raiders', state: 'Nevada', jockTax: '$0', georgiaTax: '$61,981', total: '$61,981' },
    { opponent: 'Philadelphia Eagles', state: 'Pennsylvania', jockTax: '$80,648*', georgiaTax: '$0', total: '$80,648' },
    { opponent: 'Washington Commanders', state: 'Maryland', jockTax: '$101,046**', georgiaTax: '$0', total: '$101,046' },
    { opponent: 'Minnesota Vikings', state: 'Minnesota', jockTax: '$111,035', georgiaTax: '$0', total: '$111,035' },
  ];

  return (
    <div className="min-h-screen pt-24 pb-12">
      {/* Hero */}
      <section className="px-6 md:px-12 lg:px-24 mb-12">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-6">
            <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm font-medium">
              Research Article
            </span>
            <span className="text-muted-foreground text-sm">October 23, 2025</span>
          </div>
          
          <h1 className="font-outfit text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight" data-testid="research-title">
            How Athletes' Taxes Can Solve the{' '}
            <span className="text-gradient-housing">Affordable Housing Crisis</span>
          </h1>
          
          <p className="text-xl text-muted-foreground leading-relaxed mb-8">
            A visiting scholar and former NFL and MLB player introduces an innovative solution 
            to the global housing crisis: harnessing the billions of dollars in taxes that 
            athletes and entertainers contribute annually.
          </p>

          {/* Author Card */}
          <div className="glass-card rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-6">
            <img
              src="https://customer-assets.emergentagent.com/job_affordhome-1/artifacts/ckxv90gi_vernand-morency.jpg"
              alt="Vernand Morency"
              className="w-24 h-24 rounded-full object-cover"
            />
            <div className="text-center sm:text-left">
              <h3 className="font-outfit text-xl font-semibold">Vernand Morency</h3>
              <p className="text-primary font-medium">2025 Global Sports Fellow</p>
              <p className="text-muted-foreground text-sm">
                Weatherhead Research Cluster on Global History, Harvard University
              </p>
              <p className="text-muted-foreground text-sm mt-1">
                Former NFL & MLB Professional Athlete
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="px-6 md:px-12 lg:px-24 mb-16">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-2xl overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=600&fit=crop"
              alt="Housing development concept"
              className="w-full h-64 md:h-96 object-cover"
            />
          </div>
          <p className="text-sm text-muted-foreground mt-3 text-center italic">
            The intersection of international sports, taxation, and community development offers 
            an unexpected solution to the global housing crisis.
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
            <p className="text-lg leading-relaxed">
              In the interconnected world of entertainment and professional sports, superstars like 
              Lionel Messi, Cristiano Ronaldo, Taylor Swift, and LeBron James captivate millions 
              across continents. Yet behind their public personas lies a complex financial reality: 
              international athlete and entertainer taxation systems that generate substantial revenue.
            </p>
            
            <p className="text-lg leading-relaxed">
              This international tax system is <em>in addition</em> to state and federal taxation from 
              their home countries. When Taylor Swift embarks on her global tours, performing in 
              stadiums from London to Tokyo, she becomes subject to a complex array of international 
              tax obligations. Similarly, when Messi plays for Inter Miami but travels to compete 
              internationally, or when Premier League footballers participate in Champions League 
              matches across Europe, they trigger taxation systems in multiple jurisdictions.
            </p>

            <p className="text-lg leading-relaxed">
              As a former professional athlete, I have witnessed firsthand the taxation complexities 
              that come with competing at the highest levels across multiple jurisdictions. The 
              international athlete taxation systems, which generate billions of dollars annually, 
              can be harnessed to drive meaningful change in global housing policy. In my research, 
              I explore the mechanics of how this could work, and I look to promising housing policies 
              and taxation schemes from around the world as models to guide reforms.
            </p>
          </div>

          {/* Key Stat Highlight */}
          <div className="glass-card rounded-2xl p-8 mb-12 border-l-4 border-l-secondary">
            <div className="flex items-start gap-4">
              <Globe2 className="w-8 h-8 text-secondary flex-shrink-0 mt-1" />
              <div>
                <p className="text-2xl font-bold mb-2">96,000 homes per day</p>
                <p className="text-muted-foreground">
                  According to UN-Habitat, the world needs to build 96,000 affordable homes every day 
                  to house the three billion people who will need adequate housing by 2030.
                </p>
              </div>
            </div>
          </div>

          {/* Section: Hidden Revenue Stream */}
          <section className="mb-16">
            <h2 className="font-outfit text-3xl font-bold mb-6 flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-primary" />
              The Hidden Revenue Stream
            </h2>
            
            <p className="text-lg leading-relaxed mb-6">
              This global crisis demands global solutions. While individual nations grapple with their 
              unique housing challenges, there exists an underutilized international revenue stream 
              with transformative potential: the complex web of athlete and entertainer taxation 
              systems that span continents and generate billions in revenue annually.
            </p>

            <p className="text-lg leading-relaxed mb-6">
              The foundation for international athlete taxation lies in Article 17 of the OECD Model 
              Tax Convention, which states that income generated from an athlete's personal 
              activities—such as talks or appearances—can be taxed in the source country where 
              the activity is taking place, regardless of whether the payment is directed to 
              someone other than the athlete, such as his/her team.
            </p>

            <div className="glass-card rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-primary" />
                <span className="font-semibold">Key Statistics</span>
              </div>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  The English Premier League alone generates billions in global revenue
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  US "jock tax" generated a projected $295 million in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  European nations have implemented far more sophisticated athlete taxation regimes
                </li>
              </ul>
            </div>
          </section>

          {/* Section: Why Jock Tax is Unique */}
          <section className="mb-16">
            <h2 className="font-outfit text-3xl font-bold mb-6 flex items-center gap-3">
              <Scale className="w-8 h-8 text-primary" />
              Why the Jock Tax is Unique
            </h2>
            
            <p className="text-lg leading-relaxed mb-6">
              In the US, the jock tax represents a fundamentally different approach to taxation 
              than the income tax obligations faced by regular business travelers. While most 
              workers who travel for business enjoy significant protection from out-of-state 
              taxation, athletes and entertainers face immediate tax obligations from their 
              first day of work in any jurisdiction.
            </p>

            <p className="text-lg leading-relaxed mb-6">
              The key difference lies in what tax experts call the "twelve-day rule." When a 
              typical person travels on business to another state for several days or even weeks, 
              they usually won't pay out-of-state income tax, as long as their total workdays 
              in that jurisdiction remain below the threshold. In contrast, athletes and 
              entertainers have no such exemption and are taxed from day one in every state 
              where they perform.
            </p>

            {/* Jock Tax Table */}
            <div className="mb-8">
              <h3 className="font-outfit text-xl font-semibold mb-4">
                State and Local Income Tax Example: Kirk Cousins (2024)
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Tax obligations per road game on his $62.5 million 2024 salary
              </p>
              <div className="glass-card rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left p-4 font-semibold">Opponent</th>
                        <th className="text-left p-4 font-semibold">State</th>
                        <th className="text-right p-4 font-semibold">Jock Tax</th>
                        <th className="text-right p-4 font-semibold">GA Tax</th>
                        <th className="text-right p-4 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jockTaxTable.map((row, index) => (
                        <tr key={index} className="border-b border-border/50">
                          <td className="p-4">{row.opponent}</td>
                          <td className="p-4 text-muted-foreground">{row.state}</td>
                          <td className="p-4 text-right font-mono">{row.jockTax}</td>
                          <td className="p-4 text-right font-mono">{row.georgiaTax}</td>
                          <td className="p-4 text-right font-mono font-semibold text-primary">{row.total}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="p-4 bg-muted/20 text-xs text-muted-foreground">
                  <p>* Includes Philadelphia local income tax liability</p>
                  <p>** The Washington Commanders play in Landover, MD. Includes Prince George's county income tax liability.</p>
                  <p className="mt-2">Source: National Taxpayers Union Foundation</p>
                </div>
              </div>
            </div>

            {/* Quote Block */}
            <div className="glass-card rounded-2xl p-8 border-l-4 border-l-primary">
              <Quote className="w-8 h-8 text-primary mb-4" />
              <p className="text-lg italic mb-4">
                "Unlike, say, nurse practitioners or graphic designers, jurisdictions know exactly 
                when athletes are in town. They know how much they make. And they know how to go 
                after them."
              </p>
              <p className="text-sm text-muted-foreground">— The Hustle</p>
            </div>
          </section>

          {/* Section: Global Housing Crisis */}
          <section className="mb-16">
            <h2 className="font-outfit text-3xl font-bold mb-6 flex items-center gap-3">
              <Home className="w-8 h-8 text-primary" />
              The Global Housing Crisis: Scope and Urgency
            </h2>
            
            <p className="text-lg leading-relaxed mb-6">
              The housing affordability crisis has become a defining challenge of our time, 
              affecting developed and developing nations alike. According to the International 
              Monetary Fund's analysis, housing affordability has deteriorated dramatically 
              across major economies since the pandemic.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="glass-card rounded-xl p-6 text-center">
                <p className="text-4xl font-bold text-primary mb-2">54%</p>
                <p className="text-sm text-muted-foreground">Housing price increase in the US (2015-2024)</p>
              </div>
              <div className="glass-card rounded-xl p-6 text-center">
                <p className="text-4xl font-bold text-secondary mb-2">32%</p>
                <p className="text-sm text-muted-foreground">Housing price increase in China (2015-2024)</p>
              </div>
              <div className="glass-card rounded-xl p-6 text-center">
                <p className="text-4xl font-bold text-accent mb-2">15%</p>
                <p className="text-sm text-muted-foreground">Housing price increase in the EU (2015-2024)</p>
              </div>
            </div>

            <p className="text-lg leading-relaxed">
              According to UN-Habitat, approximately 1.6 billion people worldwide lack adequate 
              housing, and this figure could rise to three billion by 2030 if current trends 
              continue. These increases far outpace income growth in most countries, creating 
              affordability gaps that affect middle-class families who previously could expect 
              to achieve homeownership.
            </p>
          </section>

          {/* Mandela Quote Section */}
          <section className="mb-16 py-12 px-8 rounded-3xl bg-gradient-to-br from-primary/10 to-secondary/10">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="font-outfit text-2xl font-bold mb-6">
                Mandela's Vision: Sports as a Force for Good
              </h2>
              <blockquote className="text-xl md:text-2xl italic mb-6">
                "Sport has the power to change the world. It has the power to unite in a way 
                that little else does. It speaks to youth in a language they understand. 
                Sport can create hope where once there was only despair."
              </blockquote>
              <p className="text-muted-foreground">
                — Nelson Mandela, Laureus World Sports Awards, 2000
              </p>
            </div>
          </section>

          {/* Section: Athletes Leading */}
          <section className="mb-16">
            <h2 className="font-outfit text-3xl font-bold mb-6 flex items-center gap-3">
              <Users className="w-8 h-8 text-primary" />
              From Philanthropy to Policy: Athletes Leading the Way
            </h2>
            
            <p className="text-lg leading-relaxed mb-8">
              Across the globe, high-profile athletes and entertainers have created housing 
              initiatives that demonstrate what is possible when resources are directed toward 
              community needs.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="glass-card rounded-xl p-6">
                <h3 className="font-outfit text-xl font-semibold mb-2">LeBron James</h3>
                <p className="text-primary font-medium mb-3">I PROMISE Housing</p>
                <p className="text-muted-foreground text-sm">
                  A fifty-unit affordable housing complex in Akron, Ohio, featuring apartments 
                  with access to fiber-optic Wi-Fi and community facilities through the LeBron 
                  James Family Foundation.
                </p>
              </div>
              <div className="glass-card rounded-xl p-6">
                <h3 className="font-outfit text-xl font-semibold mb-2">Taylor Swift</h3>
                <p className="text-primary font-medium mb-3">Housing Security Support</p>
                <p className="text-muted-foreground text-sm">
                  Significant donations to disaster relief efforts, including housing assistance 
                  to those impacted by tornadoes in Tennessee, and a reported $100,000 donation 
                  to Habitat for Humanity.
                </p>
              </div>
              <div className="glass-card rounded-xl p-6">
                <h3 className="font-outfit text-xl font-semibold mb-2">UK Footballers</h3>
                <p className="text-primary font-medium mb-3">Legacy Foundation</p>
                <p className="text-muted-foreground text-sm">
                  Rio Ferdinand, Bobby Zamora, and Mark Noble established a foundation with 
                  projects like a £400 million development near Luton, providing up to 1,300 
                  homes including social housing.
                </p>
              </div>
            </div>
          </section>

          {/* Section: Global Solutions */}
          <section className="mb-16">
            <h2 className="font-outfit text-3xl font-bold mb-6 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-primary" />
              Global Housing Solutions That Work
            </h2>
            
            <div className="space-y-6">
              <div className="glass-card rounded-xl p-6 border-l-4 border-l-primary">
                <div className="flex items-center gap-2 mb-3">
                  <Globe2 className="w-5 h-5 text-primary" />
                  <span className="font-semibold">Austria</span>
                </div>
                <h4 className="font-outfit font-semibold mb-2">Dedicated Housing Tax</h4>
                <p className="text-muted-foreground">
                  Austrians pay a housing tax of approximately one percent of income, with 
                  employers contributing as well. The money creates Gemeindebauten (communal 
                  buildings) fully owned by cities like Vienna, and rent-controlled flats 
                  through not-for-profit developers.
                </p>
              </div>

              <div className="glass-card rounded-xl p-6 border-l-4 border-l-amber-500">
                <div className="flex items-center gap-2 mb-3">
                  <Globe2 className="w-5 h-5 text-amber-500" />
                  <span className="font-semibold">Japan</span>
                </div>
                <h4 className="font-outfit font-semibold mb-2">Form-Based Zoning</h4>
                <p className="text-muted-foreground">
                  Japan uses a nested zoning system where higher-density zones automatically 
                  permit lower-density uses like housing. Because zoning is centrally regulated 
                  and uniform nationwide, developers benefit from streamlined, predictable 
                  permitting processes, keeping housing affordable even in Tokyo.
                </p>
              </div>

              <div className="glass-card rounded-xl p-6 border-l-4 border-l-green-500">
                <div className="flex items-center gap-2 mb-3">
                  <Globe2 className="w-5 h-5 text-green-500" />
                  <span className="font-semibold">Spain</span>
                </div>
                <h4 className="font-outfit font-semibold mb-2">The "Beckham Law"</h4>
                <p className="text-muted-foreground">
                  Implemented in 2005, foreign players are taxed at 24.75 percent (vs 40%+ 
                  standard rates), attracting international football stars to La Liga while 
                  generating substantial revenue for the state.
                </p>
              </div>
            </div>
          </section>

          {/* Conclusion */}
          <section className="mb-16">
            <h2 className="font-outfit text-3xl font-bold mb-6 flex items-center gap-3">
              <Lightbulb className="w-8 h-8 text-primary" />
              Conclusion: Rethinking Revenue, Rebuilding Hope
            </h2>
            
            <p className="text-lg leading-relaxed mb-6">
              The call for international athlete tax transparency is not just about following 
              the money across borders—it is about reimagining the relationship between our 
              global cultural heroes and the worldwide communities they serve.
            </p>

            <p className="text-lg leading-relaxed mb-6">
              In the most interconnected world we have ever known, only a coordinated 
              international approach can address challenges of this magnitude. The infrastructure 
              for such coordination already exists through international sports organizations, 
              entertainment industry networks, and tax frameworks. What we need now is the 
              political will and community advocacy to transform these existing systems into 
              powerful tools for global housing security.
            </p>

            <p className="text-lg leading-relaxed">
              As LeBron James, Taylor Swift, and countless other performers have demonstrated 
              through their individual philanthropic efforts across different countries, 
              investment in housing stability creates ripple effects that strengthen entire 
              communities regardless of national boundaries. It is time for our international 
              tax policies to reflect this understanding, creating systematic channels for 
              entertainment-generated revenue to flow back into the communities that need it most.
            </p>
          </section>

          {/* Author Bio */}
          <section className="mb-12">
            <div className="glass-card rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <GraduationCap className="w-6 h-6 text-primary" />
                <h3 className="font-outfit text-xl font-semibold">About the Author</h3>
              </div>
              <div className="flex flex-col md:flex-row gap-6">
                <img
                  src="https://customer-assets.emergentagent.com/job_affordhome-1/artifacts/ckxv90gi_vernand-morency.jpg"
                  alt="Vernand Morency"
                  className="w-32 h-32 rounded-full object-cover mx-auto md:mx-0"
                />
                <div>
                  <p className="text-muted-foreground leading-relaxed">
                    <strong className="text-foreground">Vernand Morency</strong> is a distinguished 
                    business executive, serial tech entrepreneur, and AI expert, currently serving 
                    as a 2025 Global Sports Fellow with the Weatherhead Research Cluster on Global 
                    History at Harvard University. A doctoral candidate focused on the intersection 
                    of technology and community development, Morency has successfully transitioned 
                    from an elite multisport professional career with the Colorado Rockies, Houston 
                    Texans, and Green Bay Packers to a dynamic leadership role in the business world.
                  </p>
                  <div className="mt-4">
                    <Link
                      to="/vernand-morency"
                      className="text-primary hover:underline font-medium inline-flex items-center gap-1"
                    >
                      Read full biography
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Original Source */}
          <section className="mb-12">
            <div className="glass-card rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-primary" />
                <div>
                  <p className="font-medium">Originally published at</p>
                  <p className="text-sm text-muted-foreground">
                    Weatherhead Center for International Affairs, Harvard University
                  </p>
                </div>
              </div>
              <a
                href="https://epicenter.wcfia.harvard.edu/article/how-athletes-taxes-can-solve-affordable-housing-crisis"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-medium transition-all hover:-translate-y-0.5"
              >
                View Original
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </section>

          {/* Social Sharing */}
          <section className="mb-12" data-testid="article-sharing">
            <div className="glass-card rounded-xl p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Share2 className="w-6 h-6 text-primary" />
                  <span className="font-medium">Share this research</span>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href={shareLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#1DA1F2]/20 hover:bg-[#1DA1F2]/30 flex items-center justify-center transition-colors"
                    title="Share on Twitter"
                  >
                    <Twitter className="w-5 h-5 text-[#1DA1F2]" />
                  </a>
                  <a
                    href={shareLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#0A66C2]/20 hover:bg-[#0A66C2]/30 flex items-center justify-center transition-colors"
                    title="Share on LinkedIn"
                  >
                    <Linkedin className="w-5 h-5 text-[#0A66C2]" />
                  </a>
                  <a
                    href={shareLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#1877F2]/20 hover:bg-[#1877F2]/30 flex items-center justify-center transition-colors"
                    title="Share on Facebook"
                  >
                    <Facebook className="w-5 h-5 text-[#1877F2]" />
                  </a>
                  <a
                    href={shareLinks.email}
                    className="w-10 h-10 rounded-full bg-secondary/20 hover:bg-secondary/30 flex items-center justify-center transition-colors"
                    title="Share via Email"
                  >
                    <Mail className="w-5 h-5 text-secondary" />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </article>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 mt-16">
        <div className="max-w-4xl mx-auto text-center glass-card rounded-3xl p-12">
          <h2 className="font-outfit text-3xl font-bold mb-4">
            Join the Movement
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Be part of the conversation at the The Jock Tax Summit. Learn how we can 
            transform athlete taxation into a powerful tool for global housing security.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
            >
              Register for Conference
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/speakers"
              className="inline-flex items-center gap-2 px-8 py-4 glass-card rounded-full font-semibold transition-all hover:bg-white/10"
            >
              Meet the Speakers
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResearchArticlePage;
